import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { get as _get } from 'lodash';
import {
    format as D3format,
    scaleLinear as D3scaleLinear,
} from 'd3';
import Moment from 'moment';

import { GetISODateFromQuick } from '../../../../libraries/helpers';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import DateRange from './../components/date-range';
import SparklineGrid from './../components/responsive-sparkline-grid';
import FilterBuilder from '../../../../components/filter/filterbuilder';

import DashboardService from '../../../../data/dashboard-service';
import DataContext from '../../../../data/data-context.js';
import AlertsService from '../../../../data/alerts-service';
import { lavender } from '../../../../data/colors';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';
import { VerifyDates } from '../../widget';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '100%',
        justifyContent: 'left',
        marginTop: 20,
        textAlign: 'left',
    },
    bar: {
        display: 'inline-block',
        marginTop: 0,
        height: 7,
        position: 'relative',
        top: -7,
    },
    bars: {
        listStyleType: 'none',
        paddingLeft: '0',
        paddingTop: 14,
    },
    // editor classes
    editorRoot: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    tagsButton: {
        width: 99,
    },
    filterText: {
        marginRight: 20,
        overflow: 'hidden',
        whiteSpace: 'break-spaces',
    },
    deleteButton: {
        paddingRight: '20px !important',
        '& button': {
            float: 'right',
        },
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    series: {
        backgroundColor: theme.palette.background.paper,
    },
    seriesList: {
        maxHeight: 230,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
}));

// backward compatibility. new API forbids these chars in the series name
const sanitizeTitle = s => {
    return (s.title || '')
        .replaceAll(' ', '_')
        .replaceAll(',', '.')
        .replaceAll('[', '(')
        .replaceAll(']', ')')
        .replaceAll('>', '}');
};

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 16 }
    : { minW: 5, minH: 5 };

const DEFAULT_SERIES = {
    title: 'series_1',
    id: 1,
};

// don't display until size-me can figure out what our size is
const AlertsTimelineWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
AlertsTimelineWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();

    const [data, setData] = useState([{ metrics: [], timeline: [] }]);
    const [max, setMax] = useState(() => 0);
    const [height, setHeight] = useState(0);
    const navigate = useNavigate();
    const [, setErrors] = useState(0);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const alertPerms = Auth.permissions.get('Alerts');

    const [settings] = useState({
        margin: 0,
        labelWidth: 0,
        chartHeight: 40,
        scale: 'linear',
        vgrid: true,
        hgrid: true,
    });

    const barScale = () => D3scaleLinear()
        .range([0, 60])
        .domain([0, max || 0]);

    const fetch = controller => {
        props.setBusy(true, false);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        if (alertPerms.read) {
            return DashboardService.getAlertTimelineSeries({
                start,
                end,
                series: (props.data.settings.series || [DEFAULT_SERIES])
                    .map(s => ({
                        title: sanitizeTitle(s),
                        json_filter: (s.json_filter || s.keyval),
                    })),
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        // the widget expects a single timeline. need to merge the Data
        let max = 0;
        const timeline = {};
        const series = (res || [])
            .reduce((a, c, i) => {
                const key = `${c.title}_${i}`;
                // update timeline map
                _get(c, 'timeline.objects', []).forEach(t => {
                    if (!timeline[t.start]) {
                        timeline[t.start] = {
                            doc_count: t.count,
                            timestamp: t.start,
                            end: new Date(t.end),
                            series: {}
                        };
                    }
                    timeline[t.start].doc_count += t.count;
                    timeline[t.start].series[key] = t.count;
                });

                // update max group
                max = Math.max(max, _get(c, 'timeline.count', 0));

                // set current series
                return {
                    ...a,
                    [key]: {
                        key,
                        value: d => d.series[key],
                        count: _get(c, 'timeline.count', 0),
                        type: 'bar',
                        title: c.title,
                        id: ((props.data.settings.series || [])
                            .find(s => s.title === c.title) || {}).id,
                    },
                };
            }, {});

        const da = [{
            index: 0,
            key: 'alerts',
            metrics: Object.values(series).sort((a, b) => {
                if (a.count === b.count) {
                    return a.key < b.key ? -1 : 1;
                }
                return b.count - a.count;
                // return props.data.settings.order === 'Most'
                //     ? b.count - a.count
                //     : a.count - b.count;
            }),
            timeline: Object.values(timeline).sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        }];

        setHeight(da[0].metrics.length * settings.chartHeight);
        setData(da);
        setMax(max);
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const handleGroupClick = sid => {
        const s = (props.data.settings.series || []).find(s => s.id === sid);
        if (!s) return;

        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: s.json_filter ? JSON.stringify(s.json_filter) : undefined,
        };
        navigate(`/views/alerts?${ParamsSerializer(p)}`);
    };

    const handleSparklineClick = useCallback((d, k, sid) => {
        const s = (props.data.settings.series || []).find(s => s.id === sid);
        if (!s) return;

        const p = {
            start: Moment(d.timestamp).utc().toISOString(),
            end: Moment(d.end).utc().toISOString(),
            filter: s.json_filter && s.json_filter.nodes ? JSON.stringify(s.json_filter) : undefined,
        };
        navigate(`/views/alerts?${ParamsSerializer(p)}`);
    }, [navigate, props.data.settings.series]);

    useEffect(() => {
        setErrors(0);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, [props.data.id]);

    useEffect(() => {
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, [props.data.start, props.data.end, props.data.quick, props.data.id]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height, overflow: 'auto' }}>
            <Grid container
                spacing={2}
                direction="row"
                wrap='nowrap'
                justifyContent="space-between"
                style={{ width: 'calc(100% - 20px)' }}>

                <Grid item style={{ maxWidth: 150 }}>
                    <div style={{ marginTop: 20 }}>
                        {data[0].metrics.map((m, i) => (
                            <Tooltip arrow placement="top"
                                key={`${m.title}_${i}`}
                                title={m.title}>
                                <Link className={classes.label}
                                    aria-label={m.title}
                                    component="button"
                                    onClick={() => handleGroupClick(m.id)}
                                    variant="body2">
                                    {m.title}
                                </Link>
                            </Tooltip>
                        ))}
                    </div>
                </Grid>

                <SparklineGrid
                    metrics={data}
                    height={height}
                    width={props.size.width - 250}
                    start={start}
                    end={end}
                    settings={settings}
                    onClick={handleSparklineClick}
                    key={props.data.id}>
                </SparklineGrid>

                <Grid style={{ maxWidth: 100 }}>
                    <div style={{ marginTop: 20, maxWidth: 100 }}>
                        <ul className={classes.bars}>
                            {data[0].metrics.map((m, i) => (
                                <li key={`${m.key}_${i}`} style={{ paddingBottom: 0 }}>
                                    <div>
                                        <div>{D3format(',')(m.count)}</div>
                                        <div className={classes.bar}
                                            style={{
                                                width: barScale()(m.count) || 5,
                                                backgroundColor: lavender[400],
                                            }}>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Grid>

            </Grid>
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const daForm = useValidate({});

    const [kicker, setKicker] = useState(0);
    const [fields, setFields] = useState([]);
    const [title, setTitle] = useState(props.data.title);
    const [series, setSeries] = useState((props.data.settings.series || [DEFAULT_SERIES])
        .map((s, i) => ({
            id: i + 1,
            title: sanitizeTitle(s),
            json_filter: s.json_filter || s.keyval,
        })));
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });
    const { assets, sensorNames, backtestingJobsLite } = useContext(DataContext);

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                series,
            },
        });
    };

    const handleShowFilter = (i, sf) => {
        const srs = series.map((s, ii) => ({ ...s, showFilter: ii === i ? sf : undefined }));
        setSeries([...srs]);
    };

    const handleFilterChange = (i, { filter }) => {
        const srs = series.map((s, ii) => ({
            ...s,
            json_filter: ii === i ? filter : s.json_filter,
            showFilter: undefined,
        }));
        setSeries([...srs]);
    };

    const handleSeriesNameChange = (i, e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        series[i].title = e.target.value;
        setSeries([...series]);
    };

    const handleDeleteSeries = i => {
        series.splice(i, 1);
        setSeries([...series]);
    };

    const handleSeriesValidatorRemoved = validID => {
        const form = daForm.get();
        delete form[validID];
        daForm.set({ ...form });
        setKicker(current => current + 1);
    };

    const handleAddSeries = () => {
        series.push({
            title: `series_${series.length + 1}`,
            id: series.reduce((a, c) => {
                return a > c.id ? a : c.id;
            }, 0) + 1,
        });
        setSeries([...series]);
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;

        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0 && (series.length > 0)) {
            return true;
        }
        if (series.length < 1) return false;
        return checkValidity(formState);
    };

    const fetchFields = async () => {
        setFields(AlertsService.fields.filter(c => c.indexed && c.type !== 'timestamp'));
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box style={{ width: 'calc(100% - 10px)' }}>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>
                <div className={classes.editorMargin}>
                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        wrap="nowrap"
                        className={classes.editorMargin}>
                        <Grid item xs={2}>
                            Series Name
                        </Grid>
                        <Grid item xs={8}>
                            Filter
                        </Grid>
                    </Grid>
                    <div className={classes.seriesList}>
                        {series.map((s, i) => (
                            <Box key={s.id} className={classes.series}>
                                <Grid
                                    container
                                    spacing={1}
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                    wrap="nowrap"
                                    className={classes.editorMargin}>
                                    <Grid item xs={2}>
                                        <Validator
                                            aria-label="Series Name"
                                            value={s.title}
                                            valueType="snmpName"
                                            onRemoval={handleSeriesValidatorRemoved}
                                            unique={series.filter((s, j) => j !== i).map(s => s.title)}
                                            onChange={(e, validID, valid) =>
                                                handleSeriesNameChange(i, e, validID, valid)
                                            }
                                            kick={kicker}/>
                                    </Grid>
                                    <Grid item xs={9} className={classes.filterText}>
                                        <FilterBuilder
                                            onOK={f => handleFilterChange(i, f)}
                                            onShow={sf => handleShowFilter(i, sf)}
                                            show={s.showFilter}
                                            fields={fields}
                                            filter={s.json_filter}
                                            placement="right-start"
                                            button={
                                                <IconButton aria-label="filter" title="filter" size="large">
                                                    <AccountTreeIcon/>
                                                </IconButton>
                                            }/>
                                        {ReactHtmlParser(AlertsService.toEnglish(
                                            s.json_filter,
                                            true,
                                            sensorNames,
                                            assets,
                                            backtestingJobsLite,
                                        ))}
                                    </Grid>
                                    <Grid item xs={1} className={classes.deleteButton}>
                                        <IconButton
                                            aria-label="delete"
                                            title="delete"
                                            disabled={series.length < 2}
                                            onClick={() => handleDeleteSeries(i)}
                                            size="large">
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                    </div>
                </div>
                <Button
                    onClick={handleAddSeries}
                    style={{ margin: 10, float: 'right' }}
                    aria-label="add series"
                    title="add series"
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon/>}>Series
                </Button>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    AlertsTimelineWidget as default,
    DEFAULTS,
};
